

































import Vue from 'vue'
import { LMap, LTileLayer, LControl, LControlZoom } from 'vue2-leaflet'
import { Circle, CircleMarker, LatLng, LatLngBounds, Map, circle as createCircle, circleMarker as createCircleMarker } from 'leaflet'
import MapMarker from './MapMarker.vue'
import Icon from './Icon.vue'

const DEFAULT_LOCATE_RADIUS_METERS = 750
const MIN_LOCATE_RADIUS_METERS = 400
const LOCATE_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 12000,
  maximumAge: 60000
}

export default Vue.extend({
  props: {
    fixedlyPositionedElementsPaneAdditionalClassesNames: [Array, String],
    zoomControlButtonsAdditionalClassesNames: {
      type: [Array, String],
      default: 'zoom-control-buttons'
    },
    locateControlButtonsAdditionalClassesNames: {
      type: [Array, String],
      default: 'without-default-margins'
    },
    requestedBounds: LatLngBounds,
    places: Array,
    searchRadiusKm: {
      type: Number,
      default: null
    }
  },
  components: {
    LMap,
    LTileLayer,
    LControl,
    LControlZoom,
    MapMarker,
    Icon
  },
  data () {
    return {
      locating: false,
      locateFailed: false,
      userLocationMarker: null as null | CircleMarker,
      userAccuracyCircle: null as null | Circle,
      searchRadiusCircle: null as null | Circle
    }
  },
  computed: {
    map () : Map {
      return (this.$refs.map && (this.$refs.map as LMap).mapObject)!
    },
    locateButtonTitle () : string {
      if (this.locating) {
        return this.$t('locating') as string
      }
      if (this.locateFailed) {
        return this.$t('geolocation_failed') as string
      }
      return this.$t('locate_me') as string
    }
  },
  mounted () {
    this.$slots.default && this.$nextTick(() => {
      const fixedlyPositionedElementsPane = this.map.createPane('fixedly-positioned-elements')
      this.fixedlyPositionedElementsPaneAdditionalClassesNames
        && this.addClassNameOrNamesToElement(fixedlyPositionedElementsPane, this.fixedlyPositionedElementsPaneAdditionalClassesNames as string[] | string)
      fixedlyPositionedElementsPane.append(...this.$slots.default!.map(slotElement => slotElement.elm as HTMLElement))
      new MutationObserver(mutations => {
        mutations.forEach(mutationRecord => {
          const transformPropertyValue = (mutationRecord.target as HTMLElement).style.transform
          fixedlyPositionedElementsPane.style.transform = `translate3d(${transformPropertyValue.slice(transformPropertyValue.indexOf('(') + 1, -1).split(', ').map(parameter => -parseFloat(parameter) + 'px').join(', ')})`
        })
      }).observe(this.map.getPanes().mapPane, { attributes: true, attributeFilter: ['style'] })
    })
    this.$nextTick(() => {
      const zoomControlButtons = (this.$refs.zoomControlButtons as LControlZoom).mapObject.getContainer()!
      this.zoomControlButtonsAdditionalClassesNames
        && this.addClassNameOrNamesToElement(zoomControlButtons, this.zoomControlButtonsAdditionalClassesNames as string[] | string)
      zoomControlButtons.childNodes.forEach(button => {
        button.addEventListener('mousedown', this.emitZoomControlButtonPressedOrTouchedEvent)
        button.addEventListener('touchstart', this.emitZoomControlButtonPressedOrTouchedEvent)
      })
    })
    const currentBounds = this.map.getBounds()
    currentBounds.equals(this.requestedBounds) ? this.$emit('bounds-applied-and-stayed-unchanged') : this.$emit('bounds-applied-and-changed', currentBounds)
    this.$nextTick(() => this.syncSearchRadiusCircle())
  },
  watch: {
    searchRadiusKm () { this.syncSearchRadiusCircle() },
    requestedBounds () { this.$nextTick(() => this.syncSearchRadiusCircle()) }
  },
  beforeDestroy () {
    (this.$refs.zoomControlButtons as LControlZoom).mapObject.getContainer()!.childNodes.forEach(button => {
      button.removeEventListener('mousedown', this.emitZoomControlButtonPressedOrTouchedEvent)
      button.removeEventListener('touchstart', this.emitZoomControlButtonPressedOrTouchedEvent)
    })
    this.clearUserLocationLayers()
    this.clearSearchRadiusCircle()
  },
  methods: {
    addClassNameOrNamesToElement (element: HTMLElement, classNameOrNames: string | string[]) {
      Array.isArray(classNameOrNames)
        ? element.classList.add(...classNameOrNames)
        : element.classList.add(classNameOrNames)
    },
    emitBoundsRelatedEventIfNecessary (bounds: LatLngBounds) {
      if (this.requestedBounds) {
        bounds.equals(this.requestedBounds) ? this.$emit('bounds-applied-and-stayed-unchanged') : this.$emit('bounds-applied-and-changed', bounds)
      } else {
        this.$emit('bounds-changed', bounds)
      }
      this.syncSearchRadiusCircle()
    },
    emitZoomControlButtonPressedOrTouchedEvent () {
      this.$emit('zoom-control-button-pressed-or-touched')
    },
    clearUserLocationLayers () {
      if (this.userLocationMarker) {
        this.map.removeLayer(this.userLocationMarker)
        this.userLocationMarker = null
      }
      if (this.userAccuracyCircle) {
        this.map.removeLayer(this.userAccuracyCircle)
        this.userAccuracyCircle = null
      }
    },
    clearSearchRadiusCircle () {
      if (this.searchRadiusCircle) {
        this.map.removeLayer(this.searchRadiusCircle)
        this.searchRadiusCircle = null
      }
    },
    syncSearchRadiusCircle () {
      if (!this.map || this.searchRadiusKm == null || !(this.searchRadiusKm > 0)) {
        this.clearSearchRadiusCircle()
        return
      }
      const center = this.map.getCenter()
      const latLng = new LatLng(center.lat, center.lng)
      const radiusMeters = Number(this.searchRadiusKm) * 1000
      if (this.searchRadiusCircle) {
        this.searchRadiusCircle.setLatLng(latLng)
        this.searchRadiusCircle.setRadius(radiusMeters)
      } else {
        this.searchRadiusCircle = createCircle(latLng, {
          radius: radiusMeters,
          color: '#2e7d32',
          weight: 2,
          opacity: 0.7,
          fillColor: '#66bb6a',
          fillOpacity: 0.12,
          interactive: false
        }).addTo(this.map)
      }
    },
    showUserLocation (latLng: LatLng, accuracyMeters: number) {
      this.clearUserLocationLayers()
      const radius = Math.max(accuracyMeters || DEFAULT_LOCATE_RADIUS_METERS, MIN_LOCATE_RADIUS_METERS)
      this.userAccuracyCircle = createCircle(latLng, {
        radius,
        color: '#1e88e5',
        weight: 1,
        opacity: 0.45,
        fillColor: '#1e88e5',
        fillOpacity: 0.12,
        interactive: false
      }).addTo(this.map)
      this.userLocationMarker = createCircleMarker(latLng, {
        radius: 7,
        color: '#ffffff',
        weight: 2,
        fillColor: '#1e88e5',
        fillOpacity: 1,
        interactive: false
      }).addTo(this.map)
      this.map.fitBounds(latLng.toBounds(radius * 2), { maxZoom: 15, animate: true })
      this.syncSearchRadiusCircle()
    },
    locateToUserPosition () {
      if (this.locating) {
        return
      }
      this.emitZoomControlButtonPressedOrTouchedEvent()
      this.locateFailed = false
      if (!navigator.geolocation) {
        this.locateFailed = true
        return
      }
      this.locating = true
      navigator.geolocation.getCurrentPosition(
        position => {
          this.locating = false
          const latLng = new LatLng(position.coords.latitude, position.coords.longitude)
          this.showUserLocation(latLng, position.coords.accuracy)
          this.$emit('user-location-found', latLng)
        },
        () => {
          this.locating = false
          this.locateFailed = true
        },
        LOCATE_OPTIONS
      )
    }
  }
})
